import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Typography } from '@mui/material';
import { AutocompleteCDS } from '@sharedCR/coreDesignSystem/form';
import SavingOverlay from '@sharedCR/coreDesignSystem/SavingOverlay';
import {
  getConditionsTaskType,
  getSchema,
  prepareDataBeforeRequest,
} from 'app/pages/loanApplication/tabs/underwriting/tabs/loanConditions/AddConditionsForm.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FormViewerNavigationType } from 'constants/forms';
import useForm from 'hooks/useForm';

import { getLoanSpecificForms, setFormNavigationTypeAction } from 'store/forms/actions';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import {
  addLoanConditionsAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
} from 'store/loanApplications/actions';
import { selectFormLockedFieldsMap, selectFormLockedPage } from 'store/loanApplications/selectors';
import { selectTaskTypeDynamicField, selectTaskTypesOptions } from 'store/system/selectors';
import {
  selectConditionCategoryOptions,
  selectConditionSetsNotEmpty,
  selectConditionsWithNumberName,
  selectConditionsWithTaskTypes,
  selectPriorTo,
} from 'store/underwriting/selectors';

import { ConditionFilterKey } from '../../../Conditions.constants';
import { ConditionsTestIds } from '../../../Conditions.testIds';
import {
  getAvailableFilterOptions,
  getCategoryOptionLabel,
  getOfferedFilterValue,
  getPriorToOptionLabel,
} from '../../../Conditions.utils';
import { initialFormValues } from '../../AddConditionsForm.constants';
import {
  contentHeaderStyles,
  contentOuterWrapperStyles,
  contentWrapperStyles,
  emptyStateStyles,
  pickerFiltersRowStyles,
  pickerSectionStyles,
  SAVING_OVERLAY_OPACITY,
} from '../../AddConditionsForm.styles';
import {
  addTaskToCondition,
  getConditionSchemaStructureKey,
  removeTaskFromCondition,
} from '../../AddConditionsForm.taskUtils';
import {
  applyUploadedFormToConditionTask,
  getActiveTabConditionIds,
  getCreateFormDialogProps,
  getFormViewerDialogProps,
  getPreparedConditionTaskTypes,
  isConditionsPickerTab,
  removeConditionFromForm,
} from '../../AddConditionsForm.utils';
import ConditionDetailCard from '../ConditionDetailCard';

const AddConditionsContent = ({
  additionalProps: { activeTab, loanId, initialForm, customOnAssignFormClick = _.noop },
  isSaving,
  setIsFormChanged,
  setOnSave,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const conditionSetsOptions = useSelector(selectConditionSetsNotEmpty);
  const conditionsOptions = useSelector(selectConditionsWithNumberName);
  const conditions = useSelector(selectConditionsWithTaskTypes);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);
  const taskTypeOptions = useSelector(selectTaskTypesOptions);
  const priorToOptions = useSelector(selectPriorTo);
  const categoryOptions = useSelector(selectConditionCategoryOptions);

  const [filterPriorTo, setFilterPriorTo] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const lockedFieldsMap = useSelector(
    selectFormLockedFieldsMap(FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS),
  );
  const isPageLocked = useSelector(
    selectFormLockedPage(FormNames.ADD_CONDITIONS_AND_CONDITIONS_SETS),
  );

  const isDisabledByLock = isPageLocked;

  const {
    form,
    isFormChanged,
    setInForm,
    handleChange,
    setSchema,
    validateField,
    validationErrors,
    validateForm,
  } = useForm(initialForm || initialFormValues);

  const formRef = useRef(form);
  formRef.current = form;

  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onConditionDelete = useCallback(
    conditionId => {
      const updated = removeConditionFromForm({ form: formRef.current, conditionId });

      setInForm('conditionTaskTypes', updated.conditionTaskTypes);
      setInForm('conditions', updated.conditions);
      setInForm('conditionSets', updated.conditionSets);
    },
    [setInForm],
  );

  const onAddConditionTask = useCallback(
    (conditionId, taskOption) => {
      setInForm(
        'conditionTaskTypes',
        addTaskToCondition({ form: formRef.current, conditionId, taskOption }),
      );
    },
    [setInForm],
  );

  const onRemoveConditionTask = useCallback(
    (conditionId, taskTypeId) => {
      setInForm(
        'conditionTaskTypes',
        removeTaskFromCondition({ form: formRef.current, conditionId, taskTypeId }),
      );
    },
    [setInForm],
  );

  const onCreateNewForm = useCallback(
    ({ conditionId, taskTypeId }) => {
      const abortController = new AbortController();

      const onFinishFormUploading = uploadedForm => {
        dispatch(getLoanSpecificForms({ loanId, applicationId }))
          .catch(Error)
          .finally(() => {
            const newForm = applyUploadedFormToConditionTask({
              form: formRef.current,
              conditionId,
              taskTypeId,
              uploadedForm,
            });
            const onAssignClick = () => customOnAssignFormClick(newForm);

            dispatch(openDialogAction(getFormViewerDialogProps({ uploadedForm, onAssignClick })));
          });
      };

      dispatch(
        openDialogAction(
          getCreateFormDialogProps({ abortController, applicationId, onFinishFormUploading }),
        ),
      );
    },
    [applicationId, customOnAssignFormClick, dispatch, loanId],
  );

  const onFilterPriorToChange = useCallback((_event, value) => {
    setFilterPriorTo(value);
  }, []);

  const onFilterCategoryChange = useCallback((_event, value) => {
    setFilterCategory(value);
  }, []);

  const availablePriorToOptions = useMemo(
    () =>
      getAvailableFilterOptions({
        options: priorToOptions,
        conditions: conditionsOptions,
        matchKey: ConditionFilterKey.PRIOR_TO,
      }),
    [priorToOptions, conditionsOptions],
  );

  const availableCategoryOptions = useMemo(
    () =>
      getAvailableFilterOptions({
        options: categoryOptions,
        conditions: conditionsOptions,
        matchKey: ConditionFilterKey.CATEGORY,
      }),
    [categoryOptions, conditionsOptions],
  );

  const selectedPriorTo = getOfferedFilterValue({
    options: availablePriorToOptions,
    value: filterPriorTo,
  });

  const selectedCategory = getOfferedFilterValue({
    options: availableCategoryOptions,
    value: filterCategory,
  });

  if (filterPriorTo !== selectedPriorTo) {
    setFilterPriorTo(selectedPriorTo);
  }

  if (filterCategory !== selectedCategory) {
    setFilterCategory(selectedCategory);
  }

  const filteredConditionsOptions = useMemo(() => {
    const options = conditionsOptions;

    if (!selectedPriorTo && !selectedCategory) {
      return options;
    }

    return options.filter(option => {
      const matchesPriorTo = !selectedPriorTo || option.priorToTypeId === selectedPriorTo.value;
      const matchesCategory =
        !selectedCategory || option.conditionCategoryId === selectedCategory.value;

      return matchesPriorTo && matchesCategory;
    });
  }, [conditionsOptions, selectedPriorTo, selectedCategory]);

  const isConditionsTab = isConditionsPickerTab(activeTab);

  const isFiltersRowVisible =
    isConditionsTab && !!(availablePriorToOptions.length || availableCategoryOptions.length);

  const activeTabConditionIds = useMemo(
    () =>
      getActiveTabConditionIds({
        isConditionsTab,
        conditions: form.conditions,
        conditionSets: form.conditionSets,
      }),
    [isConditionsTab, form.conditions, form.conditionSets],
  );

  const preparedConditionTaskTypes = useMemo(
    () =>
      getPreparedConditionTaskTypes({
        conditionTaskTypes: form.conditionTaskTypes,
        activeTabConditionIds,
      }),
    [form.conditionTaskTypes, activeTabConditionIds],
  );

  const schemaStructureKey = useMemo(
    () => getConditionSchemaStructureKey(form.conditionTaskTypes),
    [form.conditionTaskTypes],
  );

  const onSubmit = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    const dataToSend = prepareDataBeforeRequest({
      form,
      loanId,
      taskTypeDynamicFieldOptions,
    });

    return dispatch(addLoanConditionsAction(dataToSend))
      .then(() =>
        Promise.all([
          dispatch(
            getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true }),
          ),
          dispatch(getLoanApplicationTasksAction()),
        ]),
      )
      .then(() => {
        dispatch(
          showMessageAction({ message: 'Condition(s) successfully added.', variant: 'success' }),
        );
        onClose();
      })
      .catch(Error);
  }, [validateForm, form, loanId, taskTypeDynamicFieldOptions, dispatch, onClose]);

  const headerTitle = isConditionsTab ? 'Conditions' : 'Condition Sets';
  const emptyStateLabel = isConditionsTab ? 'conditions' : 'condition sets';

  useEffect(() => {
    dispatch(setFormNavigationTypeAction(FormViewerNavigationType.STATE));
  }, [dispatch]);

  useEffect(() => {
    const updatedConditionTypes = getConditionsTaskType({
      form: formRef.current,
      detailedConditions: conditions,
      taskTypeDynamicFieldOptions,
      priorToOptions,
    });

    setInForm('conditionTaskTypes', updatedConditionTypes);
  }, [
    form.conditions,
    form.conditionSets,
    conditions,
    taskTypeDynamicFieldOptions,
    priorToOptions,
    setInForm,
  ]);

  useEffect(() => {
    setSchema(
      getSchema({
        conditionTaskTypes: formRef.current.conditionTaskTypes,
        taskTypeDynamicFieldOptions,
      }),
    );
  }, [schemaStructureKey, setSchema, taskTypeDynamicFieldOptions]);

  useEffect(() => {
    setIsFormChanged(isFormChanged);
  }, [isFormChanged, setIsFormChanged]);

  useEffect(() => {
    setOnSave(() => onSubmit);
  }, [onSubmit, setOnSave]);

  return (
    <Box sx={contentOuterWrapperStyles}>
      <Box sx={contentWrapperStyles}>
        <Box sx={contentHeaderStyles}>
          <Typography variant="medium16">{headerTitle}</Typography>
        </Box>

        <Box sx={pickerSectionStyles}>
          {isFiltersRowVisible && (
            <Box sx={pickerFiltersRowStyles}>
              <AutocompleteCDS
                dataTestId={ConditionsTestIds.FILTER_PRIOR_TO_SELECT}
                getOptionLabel={getPriorToOptionLabel}
                isHidden={!availablePriorToOptions.length}
                label="Filter: Prior To"
                name="filterPriorTo"
                options={availablePriorToOptions}
                value={selectedPriorTo}
                onChange={onFilterPriorToChange}
              />
              <AutocompleteCDS
                dataTestId={ConditionsTestIds.FILTER_CATEGORY_SELECT}
                getOptionLabel={getCategoryOptionLabel}
                isHidden={!availableCategoryOptions.length}
                label="Filter: Category"
                name="filterCategory"
                options={availableCategoryOptions}
                value={selectedCategory}
                onChange={onFilterCategoryChange}
              />
            </Box>
          )}

          <AutocompleteCDS
            multiple
            dataTestId={ConditionsTestIds.CONDITIONS_SELECT}
            disabled={isDisabledByLock || lockedFieldsMap.conditions}
            error={!!validationErrors.conditions}
            helperText={validationErrors.conditions}
            isHidden={!isConditionsTab}
            label="Conditions"
            name="conditions"
            options={filteredConditionsOptions}
            value={form.conditions}
            onChange={onAutocompleteChange}
          />

          <AutocompleteCDS
            multiple
            dataTestId={ConditionsTestIds.CONDITION_SETS_SELECT}
            disabled={isDisabledByLock || lockedFieldsMap.conditionSets}
            error={!!validationErrors.conditionSets}
            helperText={validationErrors.conditionSets}
            isHidden={isConditionsTab}
            label="Condition Sets"
            name="conditionSets"
            options={conditionSetsOptions}
            value={form.conditionSets}
            onChange={onAutocompleteChange}
          />
        </Box>

        {!preparedConditionTaskTypes.length && (
          <Box sx={emptyStateStyles}>
            <Typography color="text.secondary" variant="regular14">
              Select {emptyStateLabel} above to configure them.
            </Typography>
          </Box>
        )}

        {preparedConditionTaskTypes.map(condition => (
          <ConditionDetailCard
            categoryOptions={categoryOptions}
            condition={condition}
            handleChange={handleChange}
            isDisabledByLock={isDisabledByLock}
            key={condition.conditionId}
            lockedFieldsMap={lockedFieldsMap}
            priorToOptions={priorToOptions}
            setInForm={setInForm}
            taskTypeDynamicFieldOptions={taskTypeDynamicFieldOptions}
            taskTypeOptions={taskTypeOptions}
            validateField={validateField}
            validationErrors={validationErrors}
            onAddConditionTask={onAddConditionTask}
            onAutocompleteChange={onAutocompleteChange}
            onCreateNewForm={onCreateNewForm}
            onDelete={onConditionDelete}
            onRemoveConditionTask={onRemoveConditionTask}
          />
        ))}
      </Box>

      <SavingOverlay isOpen={isSaving} scrimOpacity={SAVING_OVERLAY_OPACITY} />
    </Box>
  );
};

AddConditionsContent.propTypes = {
  additionalProps: PropTypes.shape({
    activeTab: PropTypes.string.isRequired,
    loanId: PropTypes.string.isRequired,
    initialForm: PropTypes.object,
    customOnAssignFormClick: PropTypes.func,
  }).isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default memo(AddConditionsContent);
